import { AttachmentType } from '@app/models/enums'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import Dropzone from 'react-dropzone'
import Select from 'react-select'
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import CheckBox from '../../CheckBox'
import * as FileUploadState from '../modules/fileUpload'
import './FileUpload.scss'

interface IComponentProps {
  onDrop?: (files: any) => void
  style?: React.CSSProperties
  activeStyle?: React.CSSProperties
  disableClick?: boolean
  identifier: string
}

interface IComponentState {
  identifier: string
  tags: any
  tagsList: any
}

type ComponentProps = IComponentProps &
  FileUploadState.IFileUploadState &
  typeof FileUploadState.actionCreators

export default class FileDropzone extends React.PureComponent<
  ComponentProps,
  IComponentState
> {
  public constructor(props: ComponentProps) {
    super(props)

    this.state = {
      identifier: this.props.identifier,
      tags: [],
      tagsList: [
        {
          label: 'Survey',
          value: 1,
        },
        {
          label: 'Building Layout',
          value: 2,
        },
      ],
    }
  }

  public render() {
    return (
      <div>
        <Dropzone
          noClick={this.props.disableClick}
          onDrop={this.onDrop.bind(this)}
        >
          {({getRootProps, getInputProps, isDragActive}) => (
            <div className='text-muted dropzone' style={{...this.props.style, ...(isDragActive ? this.props.activeStyle : {})}} {...getRootProps()} >
              {this.props.children}
              <input {...getInputProps()} />
            </div>
          )}
        </Dropzone>

        {this.renderFileUploadModal()}
      </div>
    )
  }

  private onDrop(acceptedFiles: File[]) {
    // react-dropzone had a custom file object with a preview field, but they dropped that in v7.
    // We need to add the preview field back in so that we can render images in the modal.
    // See: https://react-dropzone.js.org/#!/Previews
    const previewableFiles = acceptedFiles.map((file) => Object.assign(file, {
      preview: URL.createObjectURL(file),
    }))
    acceptedFiles = previewableFiles

    if (acceptedFiles.length > 0) {
      if (this.props.onDrop) {
        this.props.onDrop(acceptedFiles)
      }
      this.props.toggleFileUploadModal(
        this.state.identifier,
        acceptedFiles,
      )
    }
  }

  private renderFileUploadModal() {
    if (!this.props.currentFile) {
      return null
    }

    const isFileTooLarge =
      this.props.currentFile.file.size > FileUploadState.MAX_FILE_UPLOAD_SIZE

    let headerText = 'Upload a file?'

    if (this.props.isInEditMode === true) {
      headerText = 'Edit File'
    } else if (this.props.pendingFileCount > 1) {
      headerText += `(and ${this.props.pendingFileCount - 1} more...)`
    }

    let tags = this.state.tags
    if (Array.isArray(this.props.currentFile.attachment.tags)) {
      tags = this.props.currentFile.attachment.tags.map((item, index) => {
        return {
          label: item,
          value: index ? index + 1 : 1,
        }
      })
    } else {
      tags = this.props.currentFile.attachment.tags
        .map((item, index) => {
          return {
            label: item,
            value: index ? index + 1 : 1,
          }
        })
        .toJS()
    }

    return (
      <Modal isOpen={this.props.isFileUploadModalOpen[this.props.identifier]}>
        <ModalHeader>{headerText}</ModalHeader>
        <ModalBody>
          {isFileTooLarge ? (
            <div>
              The file{' '}
              <span className='font-weight-bold'>
                {this.props.currentFile.attachment.title}
              </span>{' '}
              is too large to upload.
            </div>
          ) : (
            <>
              <div className='d-flex flex-column'>
                {this.renderImage()}
                <div className='form-group'>
                  <label>Title</label>
                  <input
                    className='form-control'
                    type='text'
                    value={this.props.currentFile.attachment.title}
                    onChange={(e) => this.props.editFileTitle(e.target.value)}
                  />
                </div>

                <div className='form-group'>
                  <label>Description (optional)</label>
                  <textarea
                    className='form-control'
                    value={this.props.currentFile.attachment.description}
                    onChange={(e) =>
                      this.props.editFileDescription(e.target.value)
                    }
                  />
                </div>

                <div className='form-group'>
                  <label>Tags</label>
                  <Select
                    name='tags-list'
                    value={tags}
                    isMulti
                    options={this.state.tagsList}
                    onChange={(e: any) => {
                      this.setState({ tags: e })
                      this.props.editTagList(e.map((item: any) => item.label))
                    }}
                  />
                </div>

                <div className='form-group'>
                  <CheckBox
                    onChange={(newValue) => this.props.editMakePublic(newValue)}
                    value={this.props.currentFile.attachment.isPublic}
                  >
                    Make public
                  </CheckBox>
                </div>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {this.props.pendingFileCount > 1 && (
            <Button
              color='secondary'
              onClick={() => {
                this.props.cancelAllFiles()
                this.setState({
                  tags: [],
                })
              }}
            >
              Cancel All
            </Button>
          )}
          <Button
            color='secondary'
            onClick={() => {
              this.props.cancelCurrentFile()
              this.setState({
                tags: [],
              })
            }}
          >
            {isFileTooLarge ? 'Skip' : 'Cancel'}
          </Button>
          {!isFileTooLarge && (
            <Button
              color='primary'
              onClick={() => {
                this.props.requestFileUpload(
                  this.state.identifier,
                  this.props.currentFile,
                  this.props.isInEditMode,
                )
                this.setState({
                  tags: [],
                })
              }}
            >
              {this.props.isInEditMode ? 'Save' : 'Upload'}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    )
  }

  private renderImage() {
    if (
      (this.props.currentFile.file.type &&
        this.props.currentFile.file.type.indexOf('image/') > -1) ||
      this.props.currentFile.attachment.type === AttachmentType.Photo
    ) {
      return (
        <img
          src={this.props.currentFile.file.preview}
          className='align-self-center mb-2'
          style={{ height: '100px', textAlign: 'center' }}
        />
      )
    } else {
      return (
        <div style={{ textAlign: 'center' }}>
          <FontAwesomeIcon
            icon={['fal', 'file']}
            style={{ height: '100px', fontSize: '5rem' }}
          />
        </div>
      )
    }
  }
}
